import React from 'react'
import * as Form from '../../../Form'
import { Spinner } from '../../../Spinner'
import {
  Field,
  FormFacade,
  FieldInterceptChange,
  InterceptChangeEvent,
  FieldInterceptBlur,
  InterceptBlurEvent
} from '../../types'
import { StyledInput, styles } from './styled'

interface Props {
  id?: string
  autoComplete?: string
  autoFocus?: boolean
  field?: Field<HTMLInputElement, string>
  form?: FormFacade
  interceptChange?: FieldInterceptChange<string>
  interceptBlur?: FieldInterceptBlur<string>
  label?: string
  maxLength?: number
  placeholder?: string
  type?: 'text' | 'number'
  hasAutoPlaceholder?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  isRequired?: boolean
  isReadOnly?: boolean
}

export const Input = ({
  id,
  autoComplete,
  autoFocus,
  field,
  form,
  interceptChange,
  interceptBlur,
  label,
  maxLength,
  placeholder,
  type = 'text',
  hasAutoPlaceholder,
  isRequired,
  isLoading,
  isDisabled,
  isReadOnly
}: Props) => {
  const { name, value, onChange, onBlur } = field ?? {}
  const { errors, touched } = form ?? {}
  const hasError = name && errors?.[name] && touched?.[name]
  const inputPlaceholder = placeholder ?? (hasAutoPlaceholder ? label : undefined)
  const _id = id ?? name

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!name || !form) {
      return
    }

    const changeEvent: InterceptChangeEvent<string> = {
      value: event.target.value,
      name,
      form
    }

    const shouldIntercept = interceptChange?.(changeEvent)

    if (!shouldIntercept) {
      onChange?.(event)
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!name || !form) {
      return
    }

    const blurEvent: InterceptBlurEvent<string> = {
      value: event.target.value,
      name,
      form
    }

    const shouldIntercept = interceptBlur?.(blurEvent)

    if (!shouldIntercept) {
      onBlur?.(event)
    }
  }

  return (
    <Form.Field
      hasError={hasError}
      className={`form-field form-field--input ${hasError ? 'form-has-error' : ''}`}
    >
      {label && (
        <Form.Label htmlFor={_id}>
          <span>{label}</span>
          {isRequired && <em>*</em>}
        </Form.Label>
      )}

      <StyledInput
        id={_id}
        name={name}
        type={type}
        value={value?.toString()}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isDisabled ?? isLoading}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        placeholder={inputPlaceholder}
        readOnly={isReadOnly}
      />

      {isLoading && (
        <Spinner size={20} />
      )}

      {hasError && name && (
        <Form.Error>
          {errors?.[name]}
        </Form.Error>
      )}
    </Form.Field>
  )
}

export {
  styles
}
